import { Link } from 'react-router-dom';
import type { SectionKey } from '../../api/navigation.ts';
import { KIND_WORDS, entryLink, entryState, holdsKind, minutesSaid, type ActivityEntry } from '../../activity.ts';
import { formatDate } from '../../dates.ts';
import { Sheet } from '../contacts/Sheet.tsx';
import { VisitDetails } from '../VisitDetails.tsx';
import { when } from '../communications/CommunicationsFeed.tsx';
import { Fact, Facts, FilingFacts, OrderFacts, SiteVisitFacts } from '../tasks/ClosedRecordFacts.tsx';

export const FULL_SCREEN = 'Open full screen →';

/**
 * Deploy 8 (A-#227 S2): a Timeline entry opens THIS first: a read-only card with the record's facts, as a closed task
 * shows them (A-#205: the same facts components), then "Open full screen" to the record's own place. A communication,
 * a court order and a death show the entry's own fields, which the Timeline's route already sent through its section
 * gate (ruled: no single-record routes). Nothing here the reader could not open: a facts component only where the
 * reader holds the kind's section (KIND_SECTION, the one map), and the link is entryLink's, null without it. A task or
 * an action opens its own TaskCard instead (TimelineSection).
 */
export function EntryCard({ e, clientId, sections, onClose, onVisitWhole }: {
  e: ActivityEntry; clientId: string; sections: readonly SectionKey[]; onClose: () => void; onVisitWhole: (id: string) => void;
}) {
  const link = entryLink(clientId, e, sections);
  const own = <EntryFields e={e} />;
  const facts = !holdsKind(e.kind, sections) ? own
    : e.kind === 'visit' ? <VisitDetails id={e.id} readOnly />
    : e.kind === 'site_visit' ? <SiteVisitFacts propertyId={e.ref} visitId={e.id} />
    : e.kind === 'filing' ? <FilingFacts clientId={clientId} deadlineId={e.ref} />
    : e.kind === 'commission' || e.kind === 'oath_designation'
      ? <OrderFacts clientId={clientId} orderId={e.ref} kind={e.kind} missing={own} />
      : own;
  const labelId = `entry-card-${e.id}`;
  return (
    <Sheet labelId={labelId} onClose={onClose}
           title={e.what ?? KIND_WORDS[e.kind]}>
      <div aria-label="The entry, read only">
        <p className="text-sm text-neutral-700">
          {KIND_WORDS[e.kind]}{e.department ? ` · ${e.department}` : ''} · {e.at ? when(e.at) : e.on ? formatDate(e.on) : 'Date not known'}
        </p>
        {facts}
        {link === 'visit' ? (
          <button type="button" className="mt-3 inline-flex min-h-[44px] items-center font-semibold text-brand-text underline"
                  onClick={() => onVisitWhole(e.id)}>{FULL_SCREEN}</button>
        ) : link && link !== 'card' ? (
          <Link to={link} state={entryState(e)} onClick={onClose}
                className="mt-3 inline-flex min-h-[44px] items-center font-semibold text-brand-text underline">{FULL_SCREEN}</Link>
        ) : null}
      </div>
    </Sheet>
  );
}

/** The entry's own fields, as the Timeline's route sent them (already gated), and nothing else. */
export function EntryFields({ e }: { e: ActivityEntry }) {
  const spent = minutesSaid(e.minutes);
  return (
    <Facts label="The entry">
      <Fact term="What">{e.what ?? KIND_WORDS[e.kind]}</Fact>
      <Fact term="When">{e.at ? when(e.at) : e.on ? formatDate(e.on) : 'Date not known'}</Fact>
      {e.department && <Fact term="Department">{e.department}</Fact>}
      <Fact term="By">{e.by ?? 'Someone not on record'}</Fact>
      {spent && <Fact term="Time spent">{spent}</Fact>}
      {e.note && <Fact term="Note"><span className="whitespace-pre-wrap">{e.note}</span></Fact>}
      {e.edited && <Fact term="Edited">An earlier version is kept on its own page</Fact>}
    </Facts>
  );
}
